import {
  mapDocumentSchema,
  featureSchema,
  type Coordinate,
  type MapDocument,
  type MapFeature,
} from "./index.ts";
export interface MapTemplate {
  id: string;
  name: string;
  group: string;
  description: string;
  labels: string[];
  accent: string;
}
export const templates: MapTemplate[] = [
  {
    id: "blank",
    name: "Blank map",
    group: "Start fresh",
    description: "Your place, your marks, your way.",
    labels: [],
    accent: "#245c43",
  },
  {
    id: "access",
    name: "Roads & access",
    group: "Work sites",
    description: "Approved approaches, entrances and places to avoid.",
    labels: ["Entrance", "Approved road", "No access", "Delivery point"],
    accent: "#23864a",
  },
  {
    id: "staging",
    name: "Construction staging & storage",
    group: "Work sites",
    description: "Make the yard understandable at a glance.",
    labels: ["Check-in", "Equipment", "Materials", "Unload here", "Keep clear"],
    accent: "#cf6b1d",
  },
  {
    id: "storm",
    name: "Powerline storm restoration",
    group: "Work sites",
    description:
      "Pole IDs, meeting places, identifying photos and access observations.",
    labels: [
      "Pole",
      "Crew meeting",
      "Entrance",
      "Obstruction",
      "Photo reference",
    ],
    accent: "#a74735",
  },
  {
    id: "wedding",
    name: "Wedding & destination venue",
    group: "Gatherings",
    description: "Arrival, ceremony, parking, food and the places guests need.",
    labels: ["Welcome", "Park here", "Ceremony", "Food & drinks", "Restrooms"],
    accent: "#9b6756",
  },
  {
    id: "festival",
    name: "Festival & event",
    group: "Gatherings",
    description: "Help visitors and staff find their place.",
    labels: ["Entrance", "Stage", "Food", "Information", "Staff only"],
    accent: "#8459a3",
  },
  {
    id: "parking",
    name: "Parking & valet",
    group: "Gatherings",
    description: "Entry, queueing, drop-off and designated spaces.",
    labels: ["Entrance", "Drop-off", "Valet", "Parking", "Keep clear"],
    accent: "#4f78a3",
  },
  {
    id: "sports",
    name: "Sports & tournaments",
    group: "Gatherings",
    description: "Fields, check-in and visitor orientation.",
    labels: ["Field", "Check-in", "Teams", "Restrooms", "Parking"],
    accent: "#31877c",
  },
  {
    id: "farm",
    name: "Farm & ranch",
    group: "Land & growing",
    description:
      "Gates, work areas, meeting points and everyday local knowledge.",
    labels: ["Gate", "Barn", "Meet here", "Equipment", "Keep closed"],
    accent: "#789045",
  },
  {
    id: "livestock",
    name: "Horses & livestock",
    group: "Land & growing",
    description: "Pens, barns, stalls and visitor entrances.",
    labels: ["Pen", "Barn", "Stall", "Loading", "Visitor entrance"],
    accent: "#987445",
  },
  {
    id: "nursery",
    name: "Nursery, greenhouse & tree farm",
    group: "Land & growing",
    description: "Beds, houses, rows and collection points.",
    labels: ["Row", "Greenhouse", "Collection", "Staff", "Entrance"],
    accent: "#438652",
  },
  {
    id: "gardens",
    name: "Community garden",
    group: "Land & growing",
    description: "Plots, shared tools and meeting places.",
    labels: ["Plot", "Tools", "Water", "Compost", "Meet here"],
    accent: "#6a8d40",
  },
  {
    id: "marina",
    name: "Marina & boat slips",
    group: "Places & yards",
    description: "Find the right dock, slip and meeting point.",
    labels: ["Dock", "Slip", "Office", "Meeting point", "Restricted"],
    accent: "#347c99",
  },
  {
    id: "hangars",
    name: "Airport hangars",
    group: "Places & yards",
    description: "Hangar and landside visitor orientation.",
    labels: [
      "Hangar",
      "Office",
      "Visitor parking",
      "Meeting point",
      "Restricted",
    ],
    accent: "#607c9a",
  },
  {
    id: "railyard",
    name: "Railyard orientation",
    group: "Places & yards",
    description:
      "Marked locations and local instructions; no movement authority.",
    labels: ["Area", "Office", "Meeting point", "Storage", "Restricted"],
    accent: "#606871",
  },
  {
    id: "auction",
    name: "Equipment auction",
    group: "Places & yards",
    description: "Lots, rows, check-in and collection.",
    labels: ["Lot", "Row", "Check-in", "Collection", "Office"],
    accent: "#98694e",
  },
  {
    id: "salvage",
    name: "Auto salvage yard",
    group: "Places & yards",
    description: "Rows and identifying photos for the right location.",
    labels: ["Row", "Section", "Office", "Collection", "Restricted"],
    accent: "#76664f",
  },
  {
    id: "storage",
    name: "Self-storage",
    group: "Places & yards",
    description: "Buildings, units, entrances and office locations.",
    labels: ["Building", "Unit", "Office", "Entrance", "Loading"],
    accent: "#77709b",
  },
  {
    id: "grounds",
    name: "Property, campus & grounds",
    group: "Properties",
    description:
      "Buildings and practical orientation for visitors and workers.",
    labels: ["Building", "Entrance", "Office", "Meet here", "Work area"],
    accent: "#48798d",
  },
  {
    id: "rentals",
    name: "Apartments & rental property",
    group: "Properties",
    description: "Buildings, service entries and visitor meeting spots.",
    labels: [
      "Building",
      "Office",
      "Service entrance",
      "Meeting point",
      "Parking",
    ],
    accent: "#718061",
  },
  {
    id: "retreat",
    name: "Cabins, lodges & retreats",
    group: "Properties",
    description: "Arrival, cabins and shared gathering places.",
    labels: ["Cabin", "Reception", "Gathering", "Parking", "Restrooms"],
    accent: "#857152",
  },
  {
    id: "memorial",
    name: "Cemetery & memorial gardens",
    group: "Properties",
    description: "Sections and gathering places for local orientation.",
    labels: ["Section", "Meeting point", "Office", "Entrance", "Parking"],
    accent: "#7c7f66",
  },
  {
    id: "relief",
    name: "Relief & donation distribution",
    group: "Community",
    description: "Check-in, supplies, collection and temporarily closed areas.",
    labels: ["Check-in", "Donations", "Collection", "Volunteers", "Closed"],
    accent: "#b0644a",
  },
];

export function emptyMap(title: string, templateId = "blank"): MapDocument {
  const template = templates.find((t) => t.id === templateId) || templates[0];
  return mapDocumentSchema.parse({
    title: title || template.name,
    features: [],
    starterLabels: template.labels,
    center: [-104.99, 39.74],
    zoom: 16,
    basemap: "aerial",
  });
}

/** Entirely fictional marks; never imported from the preserved operational Colorado map. */
export function exampleMap(templateId: string): MapDocument {
  const template = templates.find((t) => t.id === templateId) || templates[0];
  const center: Coordinate = [-104.72, 39.57];
  const point = (
    id: string,
    label: string,
    dx: number,
    dy: number,
    kind: MapFeature["kind"] = "point",
  ) =>
    featureSchema.parse({
      id,
      kind,
      label,
      geometry: {
        type: "Point",
        coordinates: [center[0] + dx, center[1] + dy],
      },
      color: template.accent,
      reviewState: "checked",
      privateNote:
        "Fictional sample marking. Not a real entrance or permission.",
    });
  const features: MapFeature[] = [];
  const here = (x: number, y: number): Coordinate => [
    center[0] + x * 0.001,
    center[1] + y * 0.001,
  ];
  const pin = (
    id: string,
    label: string,
    x: number,
    y: number,
    kind: MapFeature["kind"] = "point",
    note = "",
  ) => {
    const f = point(`${templateId}-${id}`, label, x * 0.001, y * 0.001, kind);
    f.publicNote = note;
    features.push(f);
    return f;
  };
  const area = (
    id: string,
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    color = template.accent,
  ) =>
    features.push(
      featureSchema.parse({
        id: `${templateId}-${id}`,
        kind: "area",
        label,
        color,
        opacity: 0.3,
        reviewState: "checked",
        publicNote:
          "Fictional planning mark. This shape does not define a property, easement or surveyed boundary.",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              here(x, y),
              here(x + w, y),
              here(x + w, y + h),
              here(x, y + h),
              here(x, y),
            ],
          ],
        },
      }),
    );
  const arrow = (id: string, x: number, y: number, rotation: number) => {
    const f = pin(id, "", x, y, "arrow");
    f.rotation = rotation;
    f.color = "#23864a";
  };
  const stop = (
    id: string,
    label: string,
    x: number,
    y: number,
    warning: MapFeature["warning"] = "no-access",
  ) => {
    const f = pin(id, label, x, y, "stop");
    f.warning = warning;
    f.color = "#d62b2b";
  };
  if (templateId === "staging") {
    pin(
      "gate",
      "DELIVERIES",
      -2.1,
      -1.1,
      "gate",
      "Fictional example: check in before unloading.",
    );
    pin("office", "Check-in", -1.45, -0.7);
    area("steel", "Steel storage", -0.6, 0.35, 1.0, 0.7, "#637b92");
    area("equipment", "Equipment", 0.9, 0.35, 1.0, 0.7, "#c89736");
    area("unload", "UNLOAD HERE", 0.3, -1.1, 0.9, 0.7, "#23864a");
    pin("meeting", "Crew meeting", -1.6, 0.65);
    pin("water", "Water / restrooms", -0.55, -1.25);
    stop("clear", "KEEP CLEAR", 1.7, -0.1);
    arrow("in", -1.5, -1.15, 0);
    arrow("unload-direction", -0.1, -0.8, 0);
  } else if (templateId === "wedding") {
    pin(
      "welcome",
      "WELCOME",
      -2,
      -1.15,
      "gate",
      "Fictional invitation map. Begin here, then follow the arrows.",
    );
    area("parking", "Guest parking", -1.4, -1.3, 1.2, 0.7, "#66839c");
    pin("accessible", "Accessible drop-off", 0.1, -0.45);
    area("ceremony", "CEREMONY", 0.15, 0.2, 1.1, 0.8, "#b49262");
    pin("food", "Food & drinks", 1.55, -0.7);
    pin("restrooms", "Restrooms", -0.75, 0.6);
    pin("hosts", "Ask us here", -1.6, -0.35);
    stop("house", "Private yard", 1.85, 0.8);
    arrow("arrive", -1.4, -1.5, 0);
    arrow("walk", -0.05, -0.65, 300);
  } else if (templateId === "farm") {
    pin("gate", "GATE A · keep closed", -2, -0.6, "gate");
    area("barn", "Barn / feed", -1.1, -0.2, 0.8, 0.65, "#a76e49");
    area("equipment", "Equipment", 0.35, -1.0, 0.85, 0.55, "#c89736");
    pin("meeting", "Meet here", -1.25, -1.1);
    pin("water", "Water tank", 0.9, 0.9);
    pin("gate-b", "GATE B · livestock", 1.6, -0.2, "gate");
    stop("pond", "Pond · stay back", 1.4, 1.2, "do-not-cross");
    pin("bales", "Hay collection", -1.15, 1.0);
    arrow("barn-direction", -1.6, -0.35, 0);
    arrow("equipment-direction", -0.2, -0.9, 0);
  } else if (templateId === "storm") {
    pin("meet", "CREW MEETING", -1.6, -1.15, "label");
    pin(
      "gate",
      "ENTRY A",
      -1.7,
      -0.25,
      "gate",
      "Fictional access example. Confirm the current briefing before entering.",
    );
    pin("pole-101", "101", -0.6, 0.5, "structure");
    pin("pole-102", "102", 0.8, 0.9, "structure");
    pin("pole-103", "103", 1.45, -0.6, "structure");
    pin("material", "Material pickup", -0.3, -1.25, "label");
    stop("obstruction", "Obstruction", 1.45, 0.25, "do-not-cross");
    pin("gate-b", "ENTRY B", 1.75, -1.1, "gate");
    arrow("toward-101", -1.2, 0.15, 325);
    arrow("toward-102", 0.15, 0.65, 345);
    arrow("toward-103", 1.6, -0.9, 260);
  } else {
    template.labels.forEach((label, i) =>
      pin(
        `label-${i}`,
        label,
        ((i % 3) - 1) * 1.4,
        (Math.floor(i / 3) - 0.5) * 1.8,
        i === 0 ? "gate" : "point",
      ),
    );
  }
  return mapDocumentSchema.parse({
    title: `Sample · ${template.name}`,
    isExample: true,
    center,
    zoom: 16,
    starterLabels: template.labels,
    publicationProfile: templateId === "storm" ? "51time-driver" : "general",
    savedViews: [
      { id: "overview", name: "Whole example", center, zoom: 16 },
      {
        id: "entrance",
        name: "Entrance detail",
        center: here(-1.5, -0.65),
        zoom: 17.6,
      },
    ],
    features,
  });
}
